import { useState, useEffect } from 'react';
import { getEmbedUrl } from '../../data/trainingSessionHelpers';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Circle,
  Play,
  ClipboardList
} from 'lucide-react';
import { Button } from '../ui';
import { DocumentationModal } from './DocumentationModal';
import { cn } from '../../utils/cn';

export function SupersetPlayer({
  phaseNum,
  supersets,
  currentIndex,
  completedSupersets,
  existingSetData,
  onSupersetComplete,
  onSupersetSkip,
  onNavigate,
  onExit
}) {
  const [documentingSet, setDocumentingSet] = useState(null); // 1, 2, or 3
  const [localSetData, setLocalSetData] = useState(() => {
    // Initialize from existing data if available
    return existingSetData[supersets[currentIndex]?.id] || {};
  });

  // Reset local state when superset changes
  useEffect(() => {
    setLocalSetData(existingSetData[supersets[currentIndex]?.id] || {});
    setDocumentingSet(null);
  }, [currentIndex]);

  const currentSuperset = supersets[currentIndex];
  const progress = ((currentIndex + 1) / supersets.length) * 100;

  // Get completed sets for current superset
  const completedSetsForCurrent = [1, 2, 3].filter(
    setNum => localSetData[`set${setNum}`]
  );

  // Update local set data when navigating
  const handleNavigate = (index) => {
    // Save current data before navigating
    if (Object.keys(localSetData).length > 0) {
      onSupersetComplete(currentSuperset.id, localSetData);
    }

    // Load data for new superset
    setLocalSetData(existingSetData[supersets[index]?.id] || {});
    onNavigate(index);
  };

  const handleDocumentSave = (formData, setsToSave) => {
    const newSetData = { ...localSetData };

    setsToSave.forEach(setNum => {
      newSetData[`set${setNum}`] = formData;
    });

    setLocalSetData(newSetData);
  };

  const handleComplete = () => {
    onSupersetComplete(currentSuperset.id, localSetData);
  };

  const handleSkip = () => {
    onSupersetSkip();
    // Reset local data for next superset
    setLocalSetData(existingSetData[supersets[currentIndex + 1]?.id] || {});
  };

  if (!currentSuperset) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No superset data available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
          <span>Supersets</span>
          <span>{currentIndex + 1} of {supersets.length}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="h-2 rounded-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Superset Pills */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 overflow-x-auto">
        <div className="flex gap-2">
          {supersets.map((superset, idx) => {
            const isActive = idx === currentIndex;
            const isCompleted = completedSupersets.includes(superset.id);

            return (
              <button
                key={superset.id}
                onClick={() => handleNavigate(idx)}
                className={cn(
                  "h-8 w-8 rounded-full text-sm font-medium flex items-center justify-center transition-colors flex-shrink-0",
                  isActive && "bg-primary text-white",
                  isCompleted && !isActive && "bg-green-100 text-green-700",
                  !isActive && !isCompleted && "bg-gray-100 text-gray-500"
                )}
              >
                {isCompleted ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  idx + 1
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Superset Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto">
          {/* Superset Description */}
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            {currentSuperset.description}
          </h2>

          {/* Exercise 1 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-4">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">
                {currentSuperset.exercise1.name}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {currentSuperset.exercise1.documentation}
              </p>
            </div>

            <div className="aspect-video bg-gray-900">
              <iframe
                src={getEmbedUrl(currentSuperset.exercise1.video)}
                title={currentSuperset.exercise1.name}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>

          {/* Exercise 2 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">
                {currentSuperset.exercise2.name}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {currentSuperset.exercise2.documentation}
              </p>
            </div>

            <div className="aspect-video bg-gray-900">
              <iframe
                src={getEmbedUrl(currentSuperset.exercise2.video)}
                title={currentSuperset.exercise2.name}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>

          {/* Set Documentation Buttons */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
            <div className="flex items-center mb-3">
              <ClipboardList className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="font-semibold text-gray-900">Document Your Sets</h3>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3].map((setNum) => {
                const isCompleted = localSetData[`set${setNum}`];

                return (
                  <button
                    key={setNum}
                    onClick={() => setDocumentingSet(setNum)}
                    className={cn(
                      "p-3 rounded-lg border-2 text-center transition-colors",
                      isCompleted
                        ? "border-green-500 bg-green-50"
                        : "border-gray-200 hover:border-primary hover:bg-primary/5"
                    )}
                  >
                    <div className="flex justify-center mb-1">
                      {isCompleted ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Circle className="h-5 w-5 text-gray-300" />
                      )}
                    </div>
                    <span className={cn(
                      "text-sm font-medium",
                      isCompleted ? "text-green-700" : "text-gray-700"
                    )}>
                      Set {setNum}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div
        className="bg-white border-t border-gray-200 p-4"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="max-w-2xl mx-auto">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => handleNavigate(currentIndex - 1)}
              disabled={currentIndex === 0}
              className="flex-1"
            >
              <ChevronLeft className="h-5 w-5 mr-1" />
              Previous
            </Button>

            <Button
              variant="ghost"
              onClick={handleSkip}
              className="px-4"
            >
              Skip
            </Button>

            <Button
              onClick={handleComplete}
              className="flex-1"
            >
              {currentIndex === supersets.length - 1 ? (
                <>
                  <CheckCircle className="h-5 w-5 mr-1" />
                  Finish
                </>
              ) : (
                <>
                  Complete
                  <ChevronRight className="h-5 w-5 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Documentation Modal */}
      <DocumentationModal
        isOpen={documentingSet !== null}
        onClose={() => setDocumentingSet(null)}
        onSave={handleDocumentSave}
        phaseNum={phaseNum}
        superset={currentSuperset}
        setNumber={documentingSet}
        existingSetData={localSetData}
        completedSets={completedSetsForCurrent}
      />
    </div>
  );
}